import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input } from "@resideo/blueprint-oneportal";
import { Spinner } from "@resideo/blueprint-react";
import {
  fetchLocations,
  updateLocation,
  type ManagedLocation,
} from "@/pages/ManageLocations/manageLocationsService";
import styles from "./LocationDetailsStep.module.css";

export interface LocationDetailsStepProps {
  onBack: () => void;
  onFinish: () => void;
  locationId?: string;
  embedded?: boolean;
  doneOnly?: boolean;
}

interface LocationFormValues {
  locationName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}

const toFormValues = (location: ManagedLocation): LocationFormValues => ({
  locationName: location.name ?? "",
  addressLine1: location.address.addressLine1 ?? "",
  addressLine2: location.address.addressLine2 ?? "",
  city: location.address.city ?? "",
  state: location.address.stateProvinceRegionCode ?? "",
  zip: location.address.zipPostalCode ?? "",
});

interface EditableLocation {
  location: ManagedLocation;
  values: LocationFormValues;
}

type RequiredLocationField = Exclude<keyof LocationFormValues, "addressLine2">;

const REQUIRED_FIELDS: RequiredLocationField[] = [
  "locationName",
  "addressLine1",
  "city",
  "state",
  "zip",
];

const REQUIRED_MESSAGE_KEYS: Record<RequiredLocationField, string> = {
  locationName: "onboardingTour.locationStep.locationNameRequired",
  addressLine1: "onboardingTour.locationStep.addressRequired",
  city: "onboardingTour.locationStep.cityRequired",
  state: "onboardingTour.locationStep.stateRequired",
  zip: "onboardingTour.locationStep.zipRequired",
};

const normalizeValues = (values: LocationFormValues): LocationFormValues => ({
  locationName: values.locationName.trim(),
  addressLine1: values.addressLine1.trim(),
  addressLine2: values.addressLine2.trim(),
  city: values.city.trim(),
  state: values.state.trim(),
  zip: values.zip.trim(),
});

const isRequiredFieldMissing = (
  values: LocationFormValues,
  field: RequiredLocationField,
): boolean => !values[field].trim();

/**
 * Final onboarding step: lets the user confirm/edit their first location's
 * details before finishing the tour.
 */
export const LocationDetailsStep: React.FC<LocationDetailsStepProps> = ({
  onBack,
  onFinish,
  locationId,
  embedded = false,
  doneOnly = false,
}) => {
  const { t } = useTranslation();
  const [locationForms, setLocationForms] = useState<EditableLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadLocations = async () => {
      try {
        const locations = await fetchLocations({ includeHealth: false });

        if (cancelled) return;

        setLocationForms(
          locations
            .filter((location) => !locationId || location.id === locationId)
            .map((location) => ({
              location,
              values: toFormValues(location),
            })),
        );
      } catch {
        if (!cancelled) {
          setError(t("onboardingTour.locationStep.loadFailed"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadLocations();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleChange =
    (index: number, field: keyof LocationFormValues) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setLocationForms((prev) =>
          prev.map((item, currentIndex) => {
            if (currentIndex !== index) {
              return item;
            }

            return {
              ...item,
              values: {
                ...item.values,
                [field]: value,
              },
            };
          }),
        );
      };

  const handleFinish = async () => {
    if (locationForms.length === 0) {
      onFinish();
      return;
    }

    const locationsToConfirm = locationForms.map((entry) => ({
      entry,
      normalizedValues: normalizeValues(entry.values),
    }));

    setIsSaving(true);
    setError(null);

    try {
      await Promise.all(
        locationsToConfirm.map(({ entry, normalizedValues }) =>
          updateLocation({
            consumerAccountId: entry.location.consumerAccountId,
            locationId: entry.location.id,
            name: normalizedValues.locationName,
            address: {
              addressLine1: normalizedValues.addressLine1,
              addressLine2: normalizedValues.addressLine2,
              city: normalizedValues.city,
              stateProvinceRegionCode: normalizedValues.state,
              zipPostalCode: normalizedValues.zip,
              countryCode: entry.location.address.countryCode ?? "US",
            },
            geoCoordinate: entry.location.geoCoordinate,
            consumerDeviceIds: entry.location.consumerDeviceIds,
            isConfirmed: true,
          }),
        ),
      );
      onFinish();
    } catch {
      setError(t("onboardingTour.locationStep.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const hasValidationErrors = locationForms.some(({ values }) =>
    REQUIRED_FIELDS.some((field) => isRequiredFieldMissing(values, field)),
  );
  const getRequiredMessage = (field: RequiredLocationField): string =>
    t(REQUIRED_MESSAGE_KEYS[field]);

  return (
    <div
      className={embedded ? styles.embedded : styles.overlay}
      role={embedded ? undefined : "dialog"}
      aria-modal={embedded ? undefined : "true"}
      aria-labelledby={embedded ? undefined : "location-details-title"}
      data-testid="onboarding-location-step"
    >
      <div className={embedded ? styles.embeddedContent : styles.content}>
        <h2 id="location-details-title" className={styles.title}>
          {t("onboardingTour.locationStep.title")}
        </h2>
        <p className={styles.subtitle}>
          {t("onboardingTour.locationStep.subtitle")}
        </p>

        {error && <p role="alert">{error}</p>}

        {isLoading ? (
          <div
            className={styles.loadingContainer}
            role="status"
            aria-label="Loading location details"
            data-testid="onboarding-location-loading"
          >
            <Spinner size="large" />
          </div>
        ) : locationForms.map((entry, index) => (
          <section key={entry.location.id} className={styles.locationSection}>
            {index > 0 && <hr className={styles.sectionDivider} />}

            <div className={styles.field}>
              <label
                className={styles.label}
                htmlFor={`onboarding-location-name-${index}`}
              >
                {`${t("onboardingTour.locationStep.locationName")} *`}
              </label>
              <Input
                id={`onboarding-location-name-${index}`}
                name={`onboarding-location-name-${index}`}
                type="text"
                autoFocus={index === 0}
                className={styles.input}
                value={entry.values.locationName}
                onChange={handleChange(index, "locationName")}
                disabled={isLoading || isSaving}
                aria-invalid={isRequiredFieldMissing(entry.values, "locationName")}
              />
              {isRequiredFieldMissing(entry.values, "locationName") && (
                <span className={styles.fieldError}>
                  {getRequiredMessage("locationName")}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label
                className={styles.label}
                htmlFor={`onboarding-address-1-${index}`}
              >
                {`${t("onboardingTour.locationStep.addressLine1")} *`}
              </label>
              <Input
                id={`onboarding-address-1-${index}`}
                name={`onboarding-address-1-${index}`}
                type="text"
                className={styles.input}
                value={entry.values.addressLine1}
                onChange={handleChange(index, "addressLine1")}
                disabled={isLoading || isSaving}
                aria-invalid={isRequiredFieldMissing(entry.values, "addressLine1")}
              />
              {isRequiredFieldMissing(entry.values, "addressLine1") && (
                <span className={styles.fieldError}>
                  {getRequiredMessage("addressLine1")}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label
                className={styles.label}
                htmlFor={`onboarding-address-2-${index}`}
              >
                {`${t("onboardingTour.locationStep.addressLine2")} (optional)`}
              </label>
              <Input
                id={`onboarding-address-2-${index}`}
                name={`onboarding-address-2-${index}`}
                type="text"
                className={styles.input}
                value={entry.values.addressLine2}
                onChange={handleChange(index, "addressLine2")}
                disabled={isLoading || isSaving}
              />
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label
                  className={styles.label}
                  htmlFor={`onboarding-city-${index}`}
                >
                  {`${t("onboardingTour.locationStep.city")} *`}
                </label>
                <Input
                  id={`onboarding-city-${index}`}
                  name={`onboarding-city-${index}`}
                  type="text"
                  className={styles.input}
                  value={entry.values.city}
                  onChange={handleChange(index, "city")}
                  disabled={isLoading || isSaving}
                  aria-invalid={isRequiredFieldMissing(entry.values, "city")}
                />
                {isRequiredFieldMissing(entry.values, "city") && (
                  <span className={styles.fieldError}>
                    {getRequiredMessage("city")}
                  </span>
                )}
              </div>
              <div className={styles.field}>
                <label
                  className={styles.label}
                  htmlFor={`onboarding-state-${index}`}
                >
                  {`${t("onboardingTour.locationStep.state")} *`}
                </label>
                <Input
                  id={`onboarding-state-${index}`}
                  name={`onboarding-state-${index}`}
                  type="text"
                  className={styles.input}
                  value={entry.values.state}
                  onChange={handleChange(index, "state")}
                  disabled={isLoading || isSaving}
                  aria-invalid={isRequiredFieldMissing(entry.values, "state")}
                />
                {isRequiredFieldMissing(entry.values, "state") && (
                  <span className={styles.fieldError}>
                    {getRequiredMessage("state")}
                  </span>
                )}
              </div>
              <div className={styles.field}>
                <label
                  className={styles.label}
                  htmlFor={`onboarding-zip-${index}`}
                >
                  {`${t("onboardingTour.locationStep.zip")} *`}
                </label>
                <Input
                  id={`onboarding-zip-${index}`}
                  name={`onboarding-zip-${index}`}
                  type="text"
                  className={styles.input}
                  value={entry.values.zip}
                  onChange={handleChange(index, "zip")}
                  disabled={isLoading || isSaving}
                  aria-invalid={isRequiredFieldMissing(entry.values, "zip")}
                />
                {isRequiredFieldMissing(entry.values, "zip") && (
                  <span className={styles.fieldError}>
                    {getRequiredMessage("zip")}
                  </span>
                )}
              </div>
            </div>
          </section>
        ))}

        <div className={styles.actions}>
          {!doneOnly && (
            <Button
              type="button"
              variant="highlightTertiary"
              className={styles.backButton}
              onClick={onBack}
              disabled={isSaving}
              data-testid="onboarding-location-back"
            >
              {t("common.back")}
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            className={`${styles.finishButton} ${doneOnly ? styles.doneButton : ""}`}
            onClick={handleFinish}
            disabled={
              isLoading ||
              isSaving ||
              (Boolean(error) && locationForms.length === 0) ||
              hasValidationErrors
            }
            data-testid="onboarding-location-finish"
          >
            {doneOnly ? t("locationGuidance.done") : t("onboardingTour.locationStep.finish")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LocationDetailsStep;
